/**
 * Importing people from a CSV or Excel file.
 *
 * Everything here runs in the browser: the file is read and checked on the user's machine, and only
 * the cleaned-up rows are sent to the CRM service (which validates every row again and is the
 * authority). This module does not touch the network.
 *
 * Flow: readSpreadsheet(file) -> rows of cells -> prepareImport(rows) -> entries ready to send,
 * each with its spreadsheet row number and any problems found.
 */
import { parseTags } from './crm';

export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_ROWS = 2000;
export const CHUNK_SIZE = 200;   // the service accepts at most 200 rows per call

// ---------------------------------------------------------------------------------------------
// Reading files
// ---------------------------------------------------------------------------------------------

/** Splits CSV text into rows of cells. Handles quotes, doubled quotes, CRLF, a BOM, and , ; or tab. */
export const parseCsv = (text) => {
  const src = String(text || '').replace(/^﻿/, '');
  const delimiter = detectDelimiter(src);
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let sawAnything = false;

  const endRow = () => {
    row.push(field);
    rows.push(row);
    row = [];
    field = '';
    sawAnything = false;
  };

  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += c;
      }
    } else if (c === '"' && field === '') {
      inQuotes = true;
      sawAnything = true;
    } else if (c === delimiter) {
      row.push(field);
      field = '';
      sawAnything = true;
    } else if (c === '\r') {
      if (src[i + 1] === '\n') i++;
      endRow();
    } else if (c === '\n') {
      endRow();
    } else {
      field += c;
      sawAnything = true;
    }
  }
  if (sawAnything || field !== '' || row.length > 0) endRow();
  return rows;
};

const detectDelimiter = (src) => {
  const firstLine = src.split(/\r?\n/, 1)[0] || '';
  const counts = { ',': 0, ';': 0, '\t': 0 };
  let quoted = false;
  for (const c of firstLine) {
    if (c === '"') quoted = !quoted;
    else if (!quoted && counts[c] !== undefined) counts[c]++;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : ',';
};

/** One cell as plain trimmed text. Excel hands back numbers, booleans and dates. */
export const cellToString = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  return String(value).trim();
};

const readAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('The file could not be read.'));
  reader.readAsText(file);
});

/**
 * Reads the first sheet of a .csv or .xlsx file into rows of text cells.
 * Throws an Error with a message that is fit to show the user.
 */
export const readSpreadsheet = async (file) => {
  if (!file) throw new Error('Choose a file first.');
  if (file.size > MAX_FILE_BYTES) throw new Error('That file is too large. The limit is 5 MB.');
  const name = String(file.name || '').toLowerCase();

  let rows;
  if (name.endsWith('.csv') || name.endsWith('.txt') || name.endsWith('.tsv')) {
    rows = parseCsv(await readAsText(file));
  } else if (name.endsWith('.xlsx')) {
    let readSheet;
    try {
      ({ readSheet } = await import('read-excel-file/browser'));
    } catch (e) {
      throw new Error('Excel files could not be opened here. Save the file as CSV and try again.');
    }
    try {
      rows = await readSheet(file);
    } catch (e) {
      throw new Error('That Excel file could not be read. Check it is a valid .xlsx file, or save it as CSV.');
    }
  } else if (name.endsWith('.xls')) {
    throw new Error('Old .xls files are not supported. Save the file as .xlsx or CSV and try again.');
  } else {
    throw new Error('Choose a CSV or Excel (.xlsx) file.');
  }
  return rows.map((r) => r.map(cellToString));
};

// ---------------------------------------------------------------------------------------------
// Recognising columns
// ---------------------------------------------------------------------------------------------

export const FIELDS = {
  firstName: { label: 'First name', aliases: ['firstname', 'first', 'givenname', 'fname'] },
  lastName: { label: 'Last name', aliases: ['lastname', 'last', 'surname', 'familyname', 'lname'] },
  fullName: { label: 'Name', aliases: ['name', 'fullname', 'contactname', 'leadname', 'clientname'] },
  email: { label: 'Email', aliases: ['email', 'emailaddress', 'emailaddr', 'primaryemail', 'emails'] },
  phone: { label: 'Phone', aliases: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'cell', 'cellphone', 'telephone', 'tel', 'primaryphone', 'phones'] },
  leadType: { label: 'Buyer or seller', aliases: ['buyerorseller', 'buyerseller', 'buyerorsellerlead', 'leadtype', 'type', 'buyingorselling', 'interest', 'lookingto', 'intent'] },
  street: { label: 'Property address', aliases: ['propertyaddress', 'propertystreet', 'streetaddress', 'address', 'address1', 'listingaddress', 'sellingaddress', 'propertytosell', 'street'] },
  unit: { label: 'Unit', aliases: ['unit', 'apt', 'apartment', 'suite', 'address2', 'propertyunit', 'unitnumber'] },
  city: { label: 'City', aliases: ['city', 'propertycity', 'town'] },
  state: { label: 'State', aliases: ['state', 'propertystate', 'st'] },
  zip: { label: 'ZIP code', aliases: ['zip', 'zipcode', 'postalcode', 'postcode', 'propertyzip'] },
  areas: { label: 'Areas of interest', aliases: ['areasofinterest', 'areas', 'cities', 'citiesofinterest', 'interestedcities', 'targetcities', 'searchareas', 'locations', 'area', 'neighborhoods', 'citiesinterested', 'interestedareas'] },
  relationship: { label: 'Relationship', aliases: ['relationship', 'relation', 'contacttype'] },
  source: { label: 'Source', aliases: ['source', 'leadsource', 'origin'] },
  tags: { label: 'Tags', aliases: ['tags', 'tag', 'labels'] },
  notes: { label: 'Notes', aliases: ['notes', 'note', 'comments', 'comment', 'remarks', 'description'] },
};

const ALIAS_TO_FIELD = Object.entries(FIELDS).reduce((acc, [key, { aliases }]) => {
  aliases.forEach((a) => { acc[a] = key; });
  return acc;
}, {});

const normalizeHeader = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Matches the header row to fields. The first column wins when two headers mean the same thing.
 * Returns { columns: { fieldKey: columnIndex }, recognized: [{ header, label }], ignored: [header] }.
 */
export const mapColumns = (headerRow) => {
  const columns = {};
  const recognized = [];
  const ignored = [];
  headerRow.forEach((header, index) => {
    const key = ALIAS_TO_FIELD[normalizeHeader(header)];
    if (key && columns[key] === undefined) {
      columns[key] = index;
      recognized.push({ header: String(header).trim(), label: FIELDS[key].label });
    } else if (String(header || '').trim() !== '') {
      ignored.push(String(header).trim());
    }
  });
  return { columns, recognized, ignored };
};

// ---------------------------------------------------------------------------------------------
// Reading values
// ---------------------------------------------------------------------------------------------

const RELATIONSHIP_LABELS = {
  lead: 'LEAD', client: 'CLIENT', pastclient: 'PAST_CLIENT', sphere: 'SPHERE', sphereofinfluence: 'SPHERE',
  vendor: 'VENDOR', referralpartner: 'REFERRAL_PARTNER', referral: 'REFERRAL_PARTNER',
};

const SOURCE_LABELS = {
  referral: 'REFERRAL', sphere: 'SPHERE', openhouse: 'OPEN_HOUSE', webform: 'WEB_FORM', website: 'WEB_FORM',
  zillow: 'ZILLOW', realtorcom: 'REALTOR_COM', realtor: 'REALTOR_COM', social: 'SOCIAL', socialmedia: 'SOCIAL',
  paidad: 'PAID_AD', ad: 'PAID_AD', signcall: 'SIGN_CALL', other: 'OTHER',
};

const squash = (v) => String(v || '').toLowerCase().replace(/[^a-z]/g, '');

export const parseLeadType = (text) => {
  const t = squash(text);
  if (!t) return { value: '' };
  if (['buyer', 'buy', 'buying', 'purchase', 'purchaser'].includes(t)) return { value: 'BUYER' };
  if (['seller', 'sell', 'selling', 'listing', 'listings'].includes(t)) return { value: 'SELLER' };
  if (['both', 'buyerandseller', 'buyerseller', 'sellerandbuyer', 'sellerbuyer', 'buyerorseller'].includes(t)) return { value: 'BOTH' };
  return { error: 'Buyer or seller must be Buyer, Seller or Both.' };
};

export const parseRelationship = (text) => {
  const t = squash(text);
  if (!t) return { value: 'LEAD' };
  return RELATIONSHIP_LABELS[t] ? { value: RELATIONSHIP_LABELS[t] } : { error: 'Relationship is not one we recognise (Lead, Client, Past client, Sphere, Vendor or Referral partner).' };
};

/** An unknown source is kept as Other rather than failing the row. Blank means "let the service decide". */
export const parseSource = (text) => {
  const t = squash(text);
  if (!t) return undefined;
  return SOURCE_LABELS[t] || 'OTHER';
};

/**
 * "Oakland; Berkeley" or "Oakland, CA, Berkeley, CA" into ["Oakland", "Berkeley"] or
 * ["Oakland, CA", "Berkeley, CA"]. Semicolons, bars and new lines always separate. When only commas
 * are used, a bare two-letter state code is attached to the city before it.
 */
export const parseAreas = (text) => {
  const raw = String(text || '').trim();
  if (!raw) return [];
  let parts;
  if (/[;|\n]/.test(raw)) {
    parts = raw.split(/[;|\n]/).map((p) => p.trim()).filter(Boolean);
  } else {
    parts = [];
    raw.split(',').map((p) => p.trim()).filter(Boolean).forEach((token) => {
      if (/^[A-Z]{2}$/.test(token) && parts.length > 0 && !/,\s*[A-Z]{2}$/.test(parts[parts.length - 1])) {
        parts[parts.length - 1] = `${parts[parts.length - 1]}, ${token}`;
      } else {
        parts.push(token);
      }
    });
  }
  const seen = new Set();
  return parts.filter((p) => {
    const k = p.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const splitList = (text) => String(text || '').split(/[;,\n/]|\s{2,}/).map((p) => p.trim()).filter(Boolean);

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** "Maria Lopez" -> first "Maria", last "Lopez". "Mary Ann Smith" -> "Mary Ann", "Smith". */
const splitFullName = (full) => {
  const t = String(full || '').trim().replace(/\s+/g, ' ');
  if (!t) return { first: '', last: '' };
  const i = t.lastIndexOf(' ');
  return i < 0 ? { first: t, last: '' } : { first: t.slice(0, i), last: t.slice(i + 1) };
};

/** A US ZIP that Excel turned into a number loses its leading zero ("02134" -> 2134). */
const fixZip = (zip) => (/^\d{4}$/.test(zip) ? `0${zip}` : zip);

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

/**
 * Turns one spreadsheet row into a create-person request and a list of problems.
 * Problems are written so the user can fix the row; they never repeat a cell's contents.
 */
export const buildEntry = (cells, columns, rowNumber) => {
  const get = (key) => (columns[key] === undefined ? '' : String(cells[columns[key]] ?? '').trim());
  const errors = [];

  let firstName = get('firstName');
  let lastName = get('lastName');
  if (!firstName && !lastName && get('fullName')) {
    const split = splitFullName(get('fullName'));
    firstName = split.first;
    lastName = split.last;
  }

  const emails = splitList(get('email'));
  const phones = splitList(get('phone'));
  emails.forEach((e) => { if (!EMAIL.test(e)) errors.push('Email is not a valid email address.'); });
  phones.forEach((p) => {
    const digits = p.replace(/\D/g, '').length;
    if (!/^[0-9+()\-.\s]+$/.test(p) || digits < 7 || digits > 15) errors.push('Phone number is not valid (7 to 15 digits).');
  });

  const lead = parseLeadType(get('leadType'));
  if (lead.error) errors.push(lead.error);
  const relationship = parseRelationship(get('relationship'));
  if (relationship.error) errors.push(relationship.error);
  const leadType = lead.value || '';

  if (!firstName && !lastName && emails.length === 0 && phones.length === 0) {
    errors.push('Needs at least a name, an email or a phone number.');
  }
  if (relationship.value === 'LEAD' && !leadType && !lead.error) {
    errors.push('A lead must be a Buyer, a Seller or Both.');
  }

  const request = {
    firstName,
    lastName,
    emails: emails.map((value, i) => ({ value, primary: i === 0 })),
    phones: phones.map((value, i) => ({ value, primary: i === 0 })),
    tags: parseTags(get('tags').replace(/;/g, ',')),
    notes: get('notes'),
    relationship: relationship.value || 'LEAD',
  };
  const source = parseSource(get('source'));
  if (source) request.source = source;
  if (leadType) request.leadType = leadType;

  const selling = leadType === 'SELLER' || leadType === 'BOTH';
  const buying = leadType === 'BUYER' || leadType === 'BOTH';
  if (selling) {
    const property = { line1: get('street'), line2: get('unit'), city: get('city'), state: get('state'), zip: fixZip(get('zip')) };
    if (Object.values(property).some(Boolean)) request.propertyAddress = property;
  }
  if (buying) {
    let areas = parseAreas(get('areas'));
    // A buyer row that only has a City column means "this city" is where they are looking.
    if (areas.length === 0 && get('city')) {
      areas = [get('state') ? `${get('city')}, ${get('state')}` : get('city')];
    }
    request.areasOfInterest = areas;
    if (areas.length > 10) errors.push('At most 10 areas of interest.');
  }

  return { rowNumber, request, errors: [...new Set(errors)] };
};

/**
 * Checks the whole file. `rows` is the sheet as rows of text, header row first.
 * Returns { problem } when the file cannot be used at all, otherwise
 * { entries, recognized, ignored, blankRows } where each entry is { rowNumber, request, errors }.
 */
export const prepareImport = (rows) => {
  const nonEmpty = rows.filter((r) => r.some((c) => String(c).trim() !== ''));
  if (nonEmpty.length === 0) return { problem: 'The file is empty.' };

  const headerIndex = rows.findIndex((r) => r.some((c) => String(c).trim() !== ''));
  const { columns, recognized, ignored } = mapColumns(rows[headerIndex]);
  const identifying = ['firstName', 'lastName', 'fullName', 'email', 'phone'].some((k) => columns[k] !== undefined);
  if (!identifying) {
    return { problem: 'We could not find a name, email or phone column. The first row must be column headings, for example "First Name", "Last Name", "Email", "Phone". Download the template to see the layout.' };
  }

  const entries = [];
  let blankRows = 0;
  for (let i = headerIndex + 1; i < rows.length; i++) {
    if (!rows[i].some((c) => String(c).trim() !== '')) {
      blankRows++;
      continue;
    }
    entries.push(buildEntry(rows[i], columns, i + 1));   // +1: spreadsheet rows start at 1
  }
  if (entries.length === 0) return { problem: 'The file has headings but no people below them.' };
  if (entries.length > MAX_ROWS) {
    return { problem: `That file has ${entries.length} rows. Import up to ${MAX_ROWS} at a time; split the file and import it in parts.` };
  }
  return { entries, recognized, ignored, blankRows };
};

export const chunk = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

// ---------------------------------------------------------------------------------------------
// Files to download
// ---------------------------------------------------------------------------------------------

/** One CSV cell. Quotes when needed, and defuses cells a spreadsheet would run as a formula. */
export const csvCell = (value) => {
  let s = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (rows) => `${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`;

export const TEMPLATE_HEADERS = ['First Name', 'Last Name', 'Email', 'Phone', 'Buyer or Seller', 'Property Address', 'Unit', 'City', 'State', 'ZIP',
  'Areas of Interest', 'Relationship', 'Source', 'Tags', 'Notes'];

export const templateCsv = () => toCsv([
  TEMPLATE_HEADERS,
  ['Maria', 'Lopez', 'maria@example.com', '415-555-0142', 'Buyer', '', '', '', '', '', 'Oakland, CA; Berkeley, CA; Alameda, CA', 'Lead', 'Open house', 'hot', 'Pre-approved'],
  ['Sam', 'Smith', 'sam@example.com', '510-555-0199', 'Seller', '12 Oak St', 'Unit 4', 'Oakland', 'CA', '94601', '', 'Lead', 'Referral', '', 'Wants to list in the spring'],
]);

/**
 * The rows that were not imported, with the reason, as CSV. `skipped` is a list of
 * { rowNumber, reason, request }.
 */
export const reportCsv = (skipped) => toCsv([
  ['Row', 'Reason', 'First name', 'Last name', 'Email', 'Phone'],
  ...skipped.map((s) => [
    s.rowNumber,
    s.reason,
    s.request?.firstName || '',
    s.request?.lastName || '',
    s.request?.emails?.[0]?.value || '',
    s.request?.phones?.[0]?.value || '',
  ]),
]);
